<template>
  <div class="step1-primary">
    <div class="top-content">
      <h4 class="title-content">
        {{ $t('stepGuideControl.stepVerifyInfo.labelAccountInfo') }}
      </h4>
      <p class="subtitle-content">
        {{ $t('stepGuideControl.stepVerifyInfo.textProvidingSome') }}
      </p>
      <div
        v-if="isWaitingVerifyInfo"
        class="warning-message"
      >
        <div class="d-flex align-items-center">
          <img
            style="margin-right: 8px"
            alt="alert"
            :src="require('@/assets/images/common/ic-alert.svg')"
          >
          <span class="mb-0">
            {{ $t('stepGuideControl.stepVerifyInfo.textYourInformationIsInReviewing') }}.
            <user-hint :hide-icon="true" />
          </span>
        </div>
      </div>

      <div
        v-if="contentRejectReason"
        class="warning-message error-message"
      >
        <div class="d-flex align-items-center mb-50">
          <img
            style="margin-right: 8px"
            alt="alert"
            :src="require('@/assets/images/common/ic-warning-red.svg')"
          >
          <p class="mb-0">
            {{ $t('stepGuideControl.stepVerifyInfo.textTheProcessForVerifying') }}
          </p>
        </div>
        <div class="d-flex">
          <span>"</span>
          <div
            class="reject-reason"
            v-html="contentRejectReason"
          />
          <span>"</span>
        </div>
        <user-hint :hide-icon="true" />
      </div>
    </div>

    <div class="form-primary-contact">
      <b-card-text class="font-weight-600">
        *{{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.noteForm') }}
      </b-card-text>
      <validation-observer
        ref="formPrimaryContact"
        v-slot="{ invalid }"
      >
        <b-form
          ref="form"
          class="repeater-form"
        >
          <b-form-group class="group-input">
            <phone-input
              :class="hasDisabled ? 'bg-input-disable' : ''"
              :phone-data.sync="phoneData"
              :disabled="hasDisabled"
              :is-error-phone-number="isErrorPhoneNumber"
              :disable-country-select="true"
              :min-length="10"
            />
          </b-form-group>

          <b-form-checkbox
            v-model="checked"
            class="mb-2"
            :disabled="hasDisabled"
          >
            {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.checkBoxPhoneNumber') }}
          </b-form-checkbox>

          <b-form-group>
            <div class="d-flex justify-content-between">
              <label
                class="form-label"
                for="zaloNumber"
              >
                {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.labelZaloNumber') }} <span class="text-danger">*</span>
              </label>
            </div>
            <validation-provider
              #default="{ errors }"
              id="zaloNumber"
              rules="verifyZaloNumber|required"
              :name="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.labelZaloNumber')"
            >
              <b-input-group
                class="input-group-merge"
                :class="errors.length > 0 ? 'is-invalid' : null"
              >
                <b-form-input
                  v-model="zaloNumber"
                  id="zalo-number"
                  name="zaloNumber"
                  class="input-height"
                  :placeholder="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.placeholderZaloNumber')"
                  :state="errors.length > 0 ? false : null"
                  :disabled="hasDisabled || checked"
                />
              </b-input-group>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <div
            v-if="hasStepGuide"
            class="product-link-form"
          >
            <validation-observer ref="formValidation">
              <b-form>
                <div
                  class="bg-white box"
                  :class="{ 'form-add-product-link': !($device && $device.mobile) }"
                >
                  <h4 class="title-content">
                    {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textWhichProduct') }}
                  </h4>

                  <b-row>
                    <b-col
                      cols="12"
                      class="input-form"
                    >
                      <b-form-group class="industry-tag">
                        <validation-provider
                          #default="{ errors }"
                          rules="required"
                          :name="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textIndustry')"
                        >
                          <div class="d-flex justify-content-between">
                            <label class="form-label">
                              {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textIndustry') }} <span class="text-danger">*</span>
                            </label>
                          </div>
                          <b-input-group
                            class="input-group-merge"
                            :class="errors.length > 0 ? 'is-invalid' : null"
                          >
                            <b-form-input
                              v-model="businessModel"
                              id="industry"
                              name="industry"
                              class="input-height"
                              :placeholder="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.placeholderIndustry')"
                              :state="errors.length > 0 ? false : null"
                              :disabled="hasDisabled"
                            />
                          </b-input-group>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>

                    <b-col
                      cols="12"
                      class="input-form"
                    >
                      <b-card-text
                        v-if="!currentProductlink && !currentShopCode"
                        class="font-weight-600 distance-form"
                        :class="isErrorProduct ? 'text-danger' : ''"
                      >
                        *{{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textPleaseEnterOneOfTwo') }}
                      </b-card-text>

                      <b-form-group
                        class="product-tag mb-0"
                        label-for="tags-validation"
                      >
                        <div class="d-flex justify-content-between">
                          <label class="form-label">
                            {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.labelProductUrl') }}
                            <span
                              v-if="!!currentProductlink"
                              class="text-danger"
                            >*</span>
                          </label>
                        </div>
                        <validation-provider
                          #default="{ errors }"
                          id="product-link"
                          :name="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.labelProductUrl')"
                          :rules="{ url, productLink, required: !!currentProductlink }"
                        >
                          <b-input-group
                            class="input-group-merge"
                            :class="errors.length > 0 ? 'is-invalid' : null"
                          >
                            <b-form-input
                              v-model.trim="productsData.productURL"
                              id="verify-product-link"
                              data-testid="verify-product-link"
                              name="product link"
                              class="input-height input-product-link"
                              :placeholder="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.placeholderProductUrl')"
                              :tabindex="1"
                              :state="errors.length > 0 ? false : null"
                              :disabled="hasDisabled"
                              @focus="onFocus"
                              @blur="onBlur"
                              @input="changeProduct"
                              @keydown.space.prevent
                            />
                          </b-input-group>
                          <div
                            class="notices-product-link"
                            :class="{ 'input-invalid': errors.length > 0 && !isFocus, 'input-focus': isFocus }"
                          >
                            <span class="title-notices">🔥🔥🔥 {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textNotices') }}:</span>
                            <ul class="list-notices">
                              <li class="item-notices">
                                <span class="content-notices">{{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.firstNotices') }}</span>
                              </li>
                              <li class="item-notices">
                                <span class="content-notices">{{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.contentSecondNotices') }}
                                  <a
                                    href="https://www.google.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-blue"
                                  >https://www.google.com</a>
                                  {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textIsDifferentFrom') }}
                                  <a
                                    href="https://google.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-blue"
                                  >https://google.com</a>. {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textInCase') }}</span>
                              </li>
                              <li class="item-notices">
                                <span class="content-notices">{{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.contentFinalNotices') }}</span>
                              </li>
                            </ul>
                          </div>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col cols="12">
                      <b-form-group class="m-0 country-tag mt-2">
                        <div class="d-flex justify-content-between">
                          <label class="form-label">
                            {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.labelTikTokShopCode') }}
                            <span
                              v-if="!!currentShopCode"
                              class="text-danger"
                            >*</span>
                            <span class="font-small-2 text-placeholder">
                              ({{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.textIfYouUseTikTokShop') }})
                            </span>
                          </label>
                        </div>
                        <validation-provider
                          #default="{ errors }"
                          id="shopCode"
                          :name="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.labelTikTokShopCode')"
                          :rules="{ required: !!currentShopCode }"
                        >
                          <b-input-group
                            class="input-group-merge"
                            :class="errors.length > 0 ? 'is-invalid' : null"
                          >
                            <b-form-input
                              v-model="shopCode"
                              id="shop-code"
                              name="shopCode"
                              class="input-height"
                              :placeholder="$t('stepGuideControl.stepVerifyInfo.formPrimaryContact.placeHolderTikTokShopCode')"
                              :state="errors.length > 0 ? false : null"
                              :disabled="hasDisabled"
                              @input="changeProduct"
                            />
                          </b-input-group>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row class="mt-2">
                    <b-col>
                      <p class="font-14 text-notes">
                        *{{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.contentNotesStep', { platform: convertPlatformName }) }}
                      </p>
                    </b-col>
                  </b-row>
                </div>
              </b-form>
            </validation-observer>
          </div>

          <div class="btn-control">
            <btn-loading
              variant-convert="btn-submit"
              type="submit"
              pill
              :loading="loading"
              @click.prevent="goNext(invalid)"
            >
              <div class="d-flex align-content-center">
                <span>{{ $t("stepGuideControl.stepVerifyInfo.formVerifyContract.textNext") }}</span>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="16"
                />
              </div>
            </btn-loading>
          </div>
        </b-form>
      </validation-observer>
    </div>
    <user-hint />
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormGroup,
  BForm,
  BFormInput,
  BInputGroup,
  BCardText,
  BRow,
  BCol,
  BFormCheckbox,
} from 'bootstrap-vue'
import { url, productLink, verifyZaloNumber } from '@validations'
import BtnLoading from '@/layouts/components/BtnLoading.vue'
import UserHint from '@/components/UserHint.vue'
import PhoneInput from '@/views/auth/PhoneInput.vue'
import { createNamespacedHelpers, mapGetters } from 'vuex'
import { toastification } from '@core/mixins/toast'
import stepGuideMixin from '@/mixins/stepGuideMixin'
import envMixin from '@/mixins/envMixin'
import { PRODUCT_STATUS, PROFILE_STATUS } from '@/constants'

const { mapActions } = createNamespacedHelpers('auth')

export default {
  name: 'ContractInfoVerificationStep1',

  components: {
    BFormGroup,
    BForm,
    BFormInput,
    BInputGroup,
    BCardText,
    BRow,
    BCol,
    BFormCheckbox,
    BtnLoading,
    UserHint,
    PhoneInput,
    ValidationProvider,
    ValidationObserver,
  },

  mixins: [toastification, stepGuideMixin, envMixin],

  data() {
    return {
      zaloNumber: '',
      checked: false,
      businessModel: '',
      shopCode: null,
      currentProductlink: '',
      currentShopCode: '',
      isFocus: false,
      isErrorProduct: false,
      isErrorPhoneNumber: false,
      phoneData: {
        countryAbbr: '',
        countryCode: '',
        phone: '',
        isValid: true,
        isRequired: false,
      },
      url,
      productLink,
      verifyZaloNumber,
      productsData: {
        action: 'add',
        productURL: '',
        targetMarkets: [],
      },
    }
  },

  computed: {
    ...mapGetters({
      status: 'auth/status',
      loading: 'auth/loading',
      message: 'auth/message',
      user: 'auth/user',
    }),

    contentRejectReason() {
      return this.user?.data?.contactInfo?.verification?.rejectedReason
    },

    hasDisabled() {
      return [PROFILE_STATUS.REVIEWING, PROFILE_STATUS.APPROVED].includes(this.user?.data?.contactInfo?.verification?.status) || this.isInfoVerified
    },
  },

  watch: {
    user: {
      handler(userInfo) {
        if (userInfo?.data) {
          if (userInfo?.data?.contactInfo) {
            this.zaloNumber = userInfo?.data?.contactInfo?.zalo || ''

            const [
              countryAbbr,
              countryCode,
              phoneNum,
            ] = userInfo?.data?.contactInfo?.phone?.split('|')

            this.phoneData.phone = phoneNum
            this.phoneData.countryAbbr = countryAbbr
            this.phoneData.countryCode = countryCode

            if (this.zaloNumber === `+${this.phoneData.countryCode} ${this.phoneData.phone}`) {
              this.checked = true
            }
          }

          this.businessModel = userInfo?.data?.industry || ''
          this.shopCode = userInfo?.data?.shopCode || null
          this.currentShopCode = userInfo?.data?.shopCode || ''

          const productLinks = userInfo?.data?.products?.productURLs
          if (productLinks) {
            const convertedUrls = Object.keys(productLinks).map(value => ({
              productUrl: value,
              ...productLinks[value],
            }))
            const arrSortDate = convertedUrls.sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt))
            const productLinkReviewing = arrSortDate.find(item => item.status === PRODUCT_STATUS.REVIEWING)
            this.currentProductlink = productLinkReviewing?.productUrl || arrSortDate?.[0]?.productUrl || ''
            this.productsData.productURL = productLinkReviewing?.productUrl || arrSortDate?.[0]?.productUrl || ''
          }
        }
      },
      deep: true,
      immediate: true,
    },

    phoneData: {
      handler(val) {
        if (val && val.phone && val.countryCode && this.checked) {
          this.zaloNumber = `+${val.countryCode} ${val.phone}`
        } else if (this.checked) {
          this.zaloNumber = ''
        }
        this.isErrorPhoneNumber = false
      },
      deep: true,
    },

    checked(val) {
      if (val) {
        this.zaloNumber = `+${this.phoneData.countryCode} ${this.phoneData.phone}`
      } else {
        this.zaloNumber = ''
      }
    },
  },

  methods: {
    ...mapActions(['updateAccount', 'getAccountInfo']),

    onFocus() {
      this.isFocus = true
    },

    onBlur() {
      this.isFocus = false
    },

    changeProduct() {
      this.isErrorProduct = !(this.productsData.productURL || this.shopCode)
    },

    validPhoneInput() {
      return !this.phoneData.phone || !this.phoneData.isValid
    },

    goNext(invalid) {
      this.changeProduct()
      if (this.phoneData.phone === '') {
        this.isErrorPhoneNumber = true
      } else {
        this.isErrorPhoneNumber = false
      }
      if (this.isInfoVerified || this.isWaitingVerifyInfo) {
        this.$emit('next')
        return
      }
      this.validationForm(invalid)
    },

    async validationForm() {
      let success = await this.$refs.formPrimaryContact.validate()
      if (this.hasStepGuide && this.$refs.formValidation) {
        const validationSuccess = await this.$refs.formValidation.validate()
        success = success && validationSuccess
      }

      if (success && this.user?.data?.contactInfo?.verification?.status === PROFILE_STATUS.REVIEWING) {
        this.$emit('next')
        return
      }

      if (
        success
        && !this.validPhoneInput()
        && (!this.hasStepGuide || !this.isErrorProduct)
      ) {
        let phoneLocale = ''
        if (this.phoneData?.phone) {
          const countryAbbr = (this.phoneData.countryAbbr || 'vn').toLowerCase()
          const countryCode = String(this.phoneData.countryCode || '')
          phoneLocale = `${countryAbbr}|${countryCode}|${this.phoneData.phone}`
        }

        const params = {
          contactInfo: {
            ...(phoneLocale && { phone: phoneLocale }),
            ...(this.zaloNumber && { zalo: this.zaloNumber }),
          },
          ...(this.shopCode && { shopCode: this.shopCode }),
          ...(this.businessModel && { industry: this.businessModel }),
          ...(this.productsData.productURL && this.hasStepGuide && { products: this.productsData }),
        }
        await this.updateAccount(params)
        if (this.status) {
          await this.getAccountInfo()
          this.$refs.formPrimaryContact.reset()
          if (this.$refs.formValidation) this.$refs.formValidation.reset()
          this.toastSuccess(this.$t('stepGuideControl.stepVerifyInfo.formVerifyContract.textUpdateSuccess'))
          this.$emit('next')
        } else {
          this.toastFailure(this.message)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.input-height {
  height: 48px;
}

.top-content {
  margin-bottom: 32px;

  .title-content {
    font-size: 18px;
    font-weight: 600;
    color: #16213e;
    margin: 0 0 8px 0;
    line-height: 1.4;
  }

  .subtitle-content {
    font-size: 14px;
    line-height: 1.5;
    color: #6b7280;
    margin: 0 0 24px 0;
  }
}

.warning-message,
.error-message {
  margin-top: 10px;
  border-radius: 10px;
  padding: 10px 12px 10px 16px;
}

.warning-message {
  background: rgba(251, 176, 59, 0.1);
}

.error-message {
  background: rgba(225, 28, 78, 0.1);
}

.distance-form {
  margin-bottom: 8px;
}

.product-link-form {
  .title-content {
    font-size: 18px;
    font-weight: 600;
    color: #16213e;
    margin: 0 0 8px 0;
    line-height: 1.4;
  }
}

.input-product-link {
  border-radius: 12px 12px 0 0 !important;
}

.notices-product-link {
  border: 1px solid #e8e9ec;
  border-radius: 0 0 12px 12px;
  border-top: none;
  padding: 14px;

  .title-notices {
    font-size: 14px;
    color: #000000;
    font-weight: 600;
  }

  .list-notices {
    margin-top: 10px;
    list-style-type: unset;
    padding-left: 1rem;

    .item-notices {
      margin-bottom: 8px;

      .content-notices {
        color: rgba(0, 0, 0, 0.7);
        font-size: 14px;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  &.input-invalid {
    border-color: #e11c4e;
  }

  &.input-focus {
    border-color: #16213e;
    box-shadow: 0 3px 10px 0 rgb(34 41 47 / 10%);
  }
}

.btn-control {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
}
</style>
